// HSK Review — browse every word and grammar point for HSK 1–4, add words
// to the Library one tap at a time, or launch a flashcard drill over the
// full level (independent of what's saved). Grammar is browse-only in v1
// per the product decision to keep the Library type unchanged.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, Check, GraduationCap, Loader2, Plus, Search, X } from 'lucide-react';
import type { LibraryWord, Word } from '@/lib/types';
import type { HskGrammarItem, HskReviewLevel, HskVocabItem } from '@/lib/hsk-review/types';
import { loadHskGrammar, loadHskVocab } from '@/lib/hsk-review/data';
import { Events, track } from '@/lib/observability';
import { ScreenBackdrop } from '@/components/visual/ScreenBackdrop';
import { SealStamp } from '@/components/visual/SealStamp';
import { InkMountains } from '@/components/visual/InkMountains';
import { InkDivider } from '@/components/visual/InkDivider';
import { C } from '@/styles/tokens';

type Section = 'words' | 'grammar';

const LEVELS: HskReviewLevel[] = [1, 2, 3, 4];
// Windowed rendering: 972 rows at HSK 4 render fine, but the initial paint
// on mobile is smoother if we mount a chunk at a time. Bumped by "Show more".
const PAGE = 80;

interface HskReviewScreenProps {
  library: LibraryWord[];
  onSaveWord: (word: Word) => void;
  onOpenFlashcards: (level: HskReviewLevel) => void;
}

export function HSKReviewScreen({ library, onSaveWord, onOpenFlashcards }: HskReviewScreenProps) {
  const [level, setLevel] = useState<HskReviewLevel>(1);
  const [section, setSection] = useState<Section>('words');
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(PAGE);

  const [vocab, setVocab] = useState<HskVocabItem[] | null>(null);
  const [grammar, setGrammar] = useState<HskGrammarItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setVisible(PAGE);
  }, [level, section, query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const p = section === 'words' ? loadHskVocab(level) : loadHskGrammar(level);
    p.then((data) => {
      if (cancelled) return;
      if (section === 'words') setVocab(data as HskVocabItem[]);
      else setGrammar(data as HskGrammarItem[]);
    })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [level, section]);

  const librarySet = useMemo(() => new Set(library.map((l) => l.hz)), [library]);

  const words = useMemo(() => {
    if (section !== 'words' || !vocab) return [];
    const q = query.trim().toLowerCase();
    if (!q) return vocab;
    return vocab.filter(
      (v) =>
        v.hz.includes(q) ||
        v.py.toLowerCase().includes(q) ||
        v.en.toLowerCase().includes(q),
    );
  }, [section, vocab, query]);

  const grammarRows = useMemo(() => {
    if (section !== 'grammar' || !grammar) return [];
    const q = query.trim().toLowerCase();
    if (!q) return grammar;
    return grammar.filter(
      (g) =>
        g.display.includes(q) ||
        g.displayEn.toLowerCase().includes(q) ||
        g.categoryEn.toLowerCase().includes(q) ||
        g.example.includes(q) ||
        g.content.includes(q),
    );
  }, [section, grammar, query]);

  const totalCount = section === 'words' ? words.length : grammarRows.length;
  const cappedCount = Math.min(visible, totalCount);

  const handleSave = useCallback(
    (v: HskVocabItem) => {
      const alreadySaved = librarySet.has(v.hz);
      if (alreadySaved) return;
      onSaveWord({ hz: v.hz, py: v.py, en: v.en });
      track(Events.WordFirstEncountered, { hz: v.hz, source: 'hsk_review' });
      track(Events.WordSaved, { hz: v.hz, source: 'hsk_review', level });
    },
    [librarySet, onSaveWord, level],
  );

  return (
    <section className="relative h-full flex flex-col overflow-hidden" style={{ background: C.cream }}>
      <ScreenBackdrop mark="习" position="tr" size={260} rotate={8} />

      <div
        className="flex items-center gap-3 px-4 py-3 relative"
        style={{ borderBottom: `1px solid ${C.blush}` }}
      >
        <h1 className="ff-display text-2xl flex-1 flex items-baseline gap-2" style={{ color: C.ink }}>
          HSK Review
          <span className="ff-zh-serif text-base opacity-50">· 复习</span>
        </h1>
      </div>

      {/* Level pills. */}
      <div className="px-4 pt-3 relative">
        <div className="flex gap-2" role="tablist" aria-label="HSK level">
          {LEVELS.map((l) => {
            const active = l === level;
            return (
              <button
                key={l}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setLevel(l)}
                className="flex-1 py-2 rounded-full ff-body text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors"
                style={{
                  background: active ? C.ink : 'white',
                  color: active ? 'white' : C.inkLight,
                  border: `1px solid ${active ? C.ink : C.mist}`,
                }}
              >
                HSK {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Words / Grammar segment. */}
      <div className="px-4 pt-3 relative">
        <div
          className="flex p-1 rounded-full"
          style={{ background: 'white', border: `1px solid ${C.mist}` }}
          role="tablist"
          aria-label="Section"
        >
          {(['words', 'grammar'] as const).map((s) => {
            const active = s === section;
            const Icon = s === 'words' ? BookOpen : GraduationCap;
            return (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSection(s)}
                className="flex-1 py-1.5 rounded-full ff-body text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
                style={{
                  background: active ? C.vermillion : 'transparent',
                  color: active ? 'white' : C.inkLight,
                }}
              >
                <Icon size={13} aria-hidden />
                {s === 'words' ? 'Words' : 'Grammar'}
                <span className="ff-zh-serif opacity-70 ml-0.5 text-[11px]">
                  {s === 'words' ? '词' : '语法'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search. */}
      <div className="px-4 pt-3 relative">
        <label className="relative block">
          <Search
            size={14}
            aria-hidden
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: C.inkSofter }}
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={section === 'words' ? 'Search 字, pinyin, or English…' : 'Search grammar…'}
            className="w-full pl-9 pr-9 py-2 rounded-full ff-body text-[13px] outline-none"
            style={{ background: 'white', border: `1px solid ${C.mist}`, color: C.ink }}
            aria-label="Search HSK entries"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center"
              style={{ background: C.paper, color: C.inkLight }}
            >
              <X size={12} aria-hidden />
            </button>
          )}
        </label>
      </div>

      {/* Practice flashcards CTA — words mode only. */}
      {section === 'words' && (
        <div className="px-4 pt-3 relative">
          <button
            type="button"
            onClick={() => onOpenFlashcards(level)}
            className="relative w-full py-3 rounded-2xl ff-body font-semibold text-white inline-flex items-center justify-center gap-2 overflow-hidden shadow-paper"
            style={{ background: C.ink }}
          >
            <InkMountains height={40} className="absolute left-0 right-0 bottom-0" tint="#FFFFFF" />
            <SealStamp char="练" size={28} rotate={8} className="absolute top-1.5 right-3" />
            <span className="relative inline-flex items-center gap-2">
              <GraduationCap size={15} aria-hidden />
              Flashcards · HSK {level}
              <span className="ff-zh-serif opacity-70 ml-1">练习</span>
            </span>
          </button>
        </div>
      )}

      <InkDivider glyph="◇" className="mx-6 mt-3" />

      {/* Meta. */}
      <div className="px-4 pt-2 flex items-baseline justify-between relative">
        <span className="ff-body text-[11px] uppercase tracking-[0.18em]" style={{ color: C.inkSofter }}>
          {loading
            ? 'Loading…'
            : section === 'words'
              ? `${totalCount} word${totalCount === 1 ? '' : 's'}`
              : `${totalCount} entr${totalCount === 1 ? 'y' : 'ies'}`}
        </span>
        {section === 'words' && (
          <span className="ff-body text-[11px]" style={{ color: C.inkSofter }}>
            <span className="ff-zh-serif" style={{ color: C.vermillion }}>●</span>{' '}
            in Library
          </span>
        )}
      </div>

      {/* List. */}
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-6 space-y-1.5 relative">
        {error && (
          <div
            role="alert"
            className="p-3 rounded-2xl ff-body text-[12px]"
            style={{ background: C.blush, color: C.vermillionDeep }}
          >
            {error}
          </div>
        )}

        {loading && !error && (
          <div className="flex items-center justify-center py-12 gap-2 ff-body text-[13px]" style={{ color: C.inkLight }}>
            <Loader2 size={14} className="animate-spin" aria-hidden /> Loading HSK {level}…
          </div>
        )}

        {!loading && !error && totalCount === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <div className="ff-zh-serif text-6xl mb-3" style={{ color: C.gold, opacity: 0.5 }} aria-hidden>
              空
            </div>
            <div className="ff-display italic text-xl mb-1" style={{ color: C.ink }}>
              Nothing matches
            </div>
            <div className="ff-body text-[12px] max-w-[240px]" style={{ color: C.inkLight }}>
              Try a different search or switch levels.
            </div>
          </div>
        )}

        {section === 'words' && words.slice(0, cappedCount).map((v) => (
          <VocabRow
            key={v.id}
            item={v}
            saved={librarySet.has(v.hz)}
            onSave={() => handleSave(v)}
          />
        ))}

        {section === 'grammar' && grammarRows.slice(0, cappedCount).map((g) => (
          <GrammarRow key={g.id} item={g} />
        ))}

        {cappedCount < totalCount && (
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE)}
            className="w-full py-3 rounded-2xl ff-body text-[12px] font-semibold uppercase tracking-[0.18em]"
            style={{ background: 'white', border: `1px solid ${C.mist}`, color: C.inkLight }}
          >
            Show more ({totalCount - cappedCount} left)
          </button>
        )}
      </div>
    </section>
  );
}

function VocabRow({
  item,
  saved,
  onSave,
}: {
  item: HskVocabItem;
  saved: boolean;
  onSave: () => void;
}) {
  return (
    <div
      className="relative flex items-center gap-3 p-3 pl-4 rounded-2xl shadow-paper overflow-hidden"
      style={{ background: C.cream, border: `1px solid ${C.mist}` }}
    >
      {saved && (
        <span
          aria-hidden
          className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r"
          style={{ background: C.vermillion }}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="ff-zh-serif text-2xl font-bold" style={{ color: C.ink }}>
            {item.hz}
          </span>
          <span className="ff-body text-xs opacity-70" style={{ color: C.ink }}>
            {item.py}
          </span>
          {item.pos && (
            <span
              className="ff-body text-[10px] uppercase tracking-[0.14em] px-1.5 py-[1px] rounded-full"
              style={{ background: C.paper, color: C.inkSofter }}
            >
              {item.pos}
            </span>
          )}
        </div>
        <div className="ff-body text-[12px] leading-snug" style={{ color: C.inkLight }}>
          {item.en}
        </div>
      </div>
      {saved ? (
        <span
          className="ff-body text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full inline-flex items-center gap-1 flex-shrink-0"
          style={{ background: C.blush, color: C.vermillionDeep }}
          aria-label={`${item.hz} is in your Library`}
        >
          <Check size={11} aria-hidden /> Saved
        </span>
      ) : (
        <button
          type="button"
          onClick={onSave}
          className="ff-body text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full inline-flex items-center gap-1 text-white flex-shrink-0"
          style={{ background: C.ink }}
          aria-label={`Add ${item.hz} to Library`}
        >
          <Plus size={11} aria-hidden strokeWidth={2.75} /> Add
        </button>
      )}
    </div>
  );
}

function GrammarRow({ item }: { item: HskGrammarItem }) {
  return (
    <div
      className="relative p-3 pl-4 rounded-2xl shadow-paper overflow-hidden"
      style={{ background: C.cream, border: `1px solid ${C.mist}` }}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="ff-zh-serif text-lg font-bold" style={{ color: C.ink }}>
          {item.display}
        </span>
        {item.displayEn && (
          <span className="ff-body text-[12px]" style={{ color: C.ink }}>
            {item.displayEn}
          </span>
        )}
        {item.categoryEn && (
          <span
            className="ff-body text-[10px] uppercase tracking-[0.14em] px-1.5 py-[1px] rounded-full"
            style={{ background: C.paper, color: C.inkSofter }}
          >
            {item.categoryEn}
          </span>
        )}
      </div>
      {item.example && (
        <div className="mt-1.5 ff-body text-[12px]" style={{ color: C.inkLight }}>
          <span className="ff-zh-serif text-base font-semibold mr-1.5" style={{ color: C.vermillion }}>
            {item.example}
          </span>
          {item.exampleEn && <span className="italic opacity-80">{item.exampleEn}</span>}
        </div>
      )}
      {item.content && (
        <div
          className="mt-1.5 ff-zh-serif text-[13px] leading-relaxed"
          style={{ color: C.inkLight }}
        >
          {item.content}
        </div>
      )}
    </div>
  );
}
